<template>
  <v-container class="align-self-start" fluid>
    <v-card flat>
      <v-card-title
        class="d-flex flex-column flex-md-row align-start align-md-center"
      >
        <div class="d-flex align-center mb-2 mb-md-0">
          <v-icon left color="primary">mdi-clipboard-text-outline</v-icon>
          <span class="headline">Registro de Bioensayos</span>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar bioensayo"
          single-line
          hide-details
          clearable
          class="mt-0 search-field"
          outlined
          dense
        ></v-text-field>
      </v-card-title>

      <v-card-text>
        <v-data-table
          v-if="!$vuetify.breakpoint.mobile"
          :headers="enhancedHeaders"
          :items="bioassays"
          :items-per-page="per_page"
          dense
          fixed-header
          :height="tableHeight"
          :loading="fetchingData"
          loading-text="Cargando bioensayos..."
          no-data-text="No se encontraron bioensayos"
          hide-default-footer
          class="bioassay-table elevation-1"
          mobile-breakpoint="0"
        >
          <template v-slot:item.serial="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span
                  v-on="on"
                  class="font-weight-bold primary--text text-no-wrap"
                >
                  {{ item.serial }}
                </span>
              </template>
              <span>ID del bioensayo</span>
            </v-tooltip>
            <v-spacer></v-spacer>
          </template>

          <template v-slot:item.productInfo="{ item }">
            <div class="d-flex flex-column">
              <div class="product-chips mb-1">
                <v-tooltip
                  v-for="(product, index) in item.products"
                  :key="index"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-chip
                      color="orange"
                      dark
                      small
                      v-on="on"
                      @click.stop="handleProductSelected(product._id)"
                      class="ma-1"
                    >
                      <v-icon left small>mdi-water</v-icon>
                      {{ truncate(product.name, 10) }}
                    </v-chip>
                  </template>
                  <span
                    >{{ product.name }} - {{ product.type || "Sin tipo" }}</span
                  >
                </v-tooltip>
              </div>

              <div class="d-flex align-center justify-space-between">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-chip
                        v-if="item.productQuantity"
                        small
                        color="blue-grey lighten-4"
                        class="mr-1"
                      >
                        {{ item.productQuantity }} kg
                      </v-chip>
                      <span v-else class="grey--text text-caption">-</span>
                    </div>
                  </template>
                  <span>Cantidad de producto requerido</span>
                </v-tooltip>

                <v-tooltip v-if="item.productReception" bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="on" class="text-caption text-no-wrap">
                      {{ formatDate(item.productReception) }}
                    </span>
                  </template>
                  <span
                    >Recepción:
                    {{ formatDateTime(item.productReception) }}</span
                  >
                </v-tooltip>
                <span v-else class="grey--text text-caption">-</span>
              </div>
            </div>
          </template>

          <template v-slot:item.soilInfo="{ item }">
            <div class="d-flex flex-column">
              <div v-if="item.soilWeight" class="d-flex align-center">
                <v-icon small class="mr-1">mdi-weight</v-icon>
                <span class="text-caption">{{ item.soilWeight }} g</span>
              </div>
              <div v-if="item.soilReception" class="d-flex align-center mt-1">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                <span class="text-caption">{{
                  formatDate(item.soilReception)
                }}</span>
              </div>
              <div
                v-if="!item.soilWeight && !item.soilReception"
                class="grey--text text-caption"
              >
                -
              </div>
            </div>
          </template>

          <template v-slot:item.doseInfo="{ item }">
            <div class="d-flex flex-column">
              <div v-if="item.dosesQuantity" class="d-flex align-center">
                <v-icon small class="mr-1">mdi-numeric</v-icon>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <span class="text-caption">{{ item.dosesQuantity }}</span>
                    </div>
                  </template>
                  <span>Cantidad de dosis</span>
                </v-tooltip>
              </div>
              <div v-if="item.doseSize" class="d-flex align-center mt-1">
                <v-icon small class="mr-1">mdi-counter</v-icon>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <span class="text-caption">{{
                        item.doseSize.map((d) => d.size).join(", ")
                      }}</span>
                    </div>
                  </template>
                  <span> Cantidad de producto por dosis </span>
                </v-tooltip>
              </div>
              <div
                v-if="!item.dosesQuantity && !item.doseSize"
                class="grey--text text-caption"
              >
                -
              </div>
            </div>
          </template>

          <template v-slot:item.replicatesPerDose="{ item }">
            <v-chip
              v-if="item.replicatesPerDose"
              small
              color="indigo lighten-5"
              class="text-center"
            >
              {{ item.replicatesPerDose }} rep.
            </v-chip>
            <span v-else class="grey--text text-caption">-</span>
          </template>

          <template v-slot:item.biologicalInfo="{ item }">
            <div class="d-flex flex-column">
              <div class="crop-chips mb-1">
                <v-tooltip
                  v-for="(crop, index) in item.crops"
                  :key="index"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-chip
                      color="green lighten-2"
                      small
                      v-on="on"
                      class="ma-1"
                    >
                      {{ truncate(getCrop(crop), 8) }}
                    </v-chip>
                  </template>
                  <span>{{ getCrop(crop) }}</span>
                </v-tooltip>
                <span
                  v-if="!item.crops || item.crops.length === 0"
                  class="grey--text text-caption"
                  >-</span
                >
              </div>

              <div class="marker-chips">
                <v-tooltip
                  v-for="(marker, index) in item.molecularMarkers"
                  :key="index"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-chip
                      color="deep-purple lighten-4"
                      small
                      v-on="on"
                      class="ma-1"
                    >
                      {{
                        marker.markerType == "MO"
                          ? truncate(marker.microorganism_meta.name, 8)
                          : truncate(marker.soilFunction.es, 8)
                      }}
                    </v-chip>
                  </template>
                  <span>
                    {{
                      marker.markerType == "MO"
                        ? marker.microorganism_meta.name
                        : marker.soilFunction.es
                    }}
                    <br />
                    Tipo:
                    {{ marker.markerType == "MO" ? "Taxonómico" : "Funcional" }}
                    <br />
                    ID: {{ marker.molecularMarkerID }}
                  </span>
                </v-tooltip>
                <span
                  v-if="
                    !item.molecularMarkers || item.molecularMarkers.length === 0
                  "
                  class="grey--text text-caption"
                  >-</span
                >
              </div>
            </div>
          </template>

          <template v-slot:item.bioassayStartDate="{ item }">
            <v-tooltip v-if="item.bioassayStartDate" bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on" class="text-caption text-no-wrap">
                  {{ formatDate(item.bioassayStartDate) }}
                </span>
              </template>
              <span>Inicio: {{ formatDateTime(item.bioassayStartDate) }}</span>
            </v-tooltip>
            <span v-else class="grey--text text-caption">-</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  dark
                  small
                  v-on="on"
                  class="status-chip"
                >
                  {{ getStatusShort(item.status) }}
                </v-chip>
              </template>
              <span>Estado: {{ getStatus(item.status) }}</span>
            </v-tooltip>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span
                  v-on="on"
                  attrs=""
                  v-if="
                    !!item.packageReference &&
                    item.packageReference.length > 0 &&
                    item.packageReference[0]
                  "
                  class="secondary--text font-italic font-weight-bold"
                >
                  {{ item.packageReference[0].name }}
                  <div v-if="item.sentNotification">
                    <v-spacer></v-spacer>
                    <span>
                      Enviado a CMDA: {{ formatDate(item.sentNotification) }}
                    </span>
                  </div>
                </span>
              </template>
              Paquete SETUP
            </v-tooltip>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <v-menu offset-y left>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" class="action-btn">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="editItem(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="blue">mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Editar</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click="results(item)"
                    :disabled="item.status == 'NEW'"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="green">mdi-chart-bar</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Resultados</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click="calibration(item)"
                    :disabled="item.status == 'NEW'"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="orange">mdi-wrench</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Calibración</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click="handleGeneratePackage(item)"
                    :disabled="
                      !!item.packageReference &&
                      item.packageReference.length > 0
                    "
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="primary">mdi-package</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>
                      <div
                        v-if="
                          !!item.packageReference &&
                          item.packageReference.length > 0
                        "
                      >
                        Generar paquete
                        <span class="red--text">(Generado)</span>
                      </div>
                      <div v-else>Generar paquete</div>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click="markAsSent(item)"
                    :disabled="!!item.sentNotification"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="teal">mdi-send-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Marcar como enviado</v-list-item-title>
                  </v-list-item>

                  <v-divider class="my-1"></v-divider>

                  <v-list-item
                    @click="deleteItem(item)"
                    :disabled="item.status == 'FINISHED'"
                  >
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="red">mdi-delete</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>
                      <div v-if="item.status == 'FINISHED'">
                        <span>Eliminar</span>
                      </div>
                      <div v-else>
                        <span class="red--text">Eliminar</span>
                      </div>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
        </v-data-table>

        <div v-else>
          <v-progress-linear
            v-if="fetchingData"
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-linear>

          <v-expansion-panels v-if="bioassays.length > 0" accordion>
            <v-expansion-panel
              v-for="item in bioassays"
              :key="item._id"
              class="mb-3 bioassay-card"
            >
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    dark
                    small
                    class="mr-2 status-chip-mobile"
                  >
                    {{ getStatusShort(item.status) }}
                  </v-chip>
                  <span class="font-weight-bold primary--text">{{
                    item.serial
                  }}</span>
                  <v-spacer></v-spacer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <span
                        v-on="on"
                        attrs=""
                        v-if="
                          !!item.packageReference &&
                          item.packageReference.length > 0 &&
                          item.packageReference[0]
                        "
                        class="secondary--text font-italic font-weight-bold"
                      >
                        {{ item.packageReference[0].name }}
                        <div v-if="item.sentNotification">
                          <v-spacer></v-spacer>
                          <span>
                            Enviado a CMDA:
                            {{ formatDate(item.sentNotification) }}
                          </span>
                        </div>
                      </span>
                    </template>
                    Paquete SETUP
                  </v-tooltip>
                </div>
                <template v-slot:actions>
                  <v-icon color="primary">$expand</v-icon>
                </template>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <div class="mb-3">
                  <div
                    class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
                  >
                    Productos
                  </div>
                  <div class="product-chips">
                    <v-chip
                      v-for="(product, index) in item.products"
                      :key="index"
                      color="orange"
                      dark
                      small
                      @click.stop="handleProductSelected(product._id)"
                      class="ma-1"
                    >
                      <v-icon left small>mdi-water</v-icon>
                      {{ truncate(product.name, 15) }}
                    </v-chip>
                  </div>
                  <div class="d-flex align-center mt-1">
                    <v-icon small class="mr-1">mdi-scale</v-icon>
                    <span class="text-caption"
                      >{{ item.productQuantity || 0 }} kg</span
                    >
                    <v-spacer></v-spacer>
                    <v-icon small class="mr-1">mdi-calendar</v-icon>
                    <span class="text-caption">{{
                      formatDate(item.productReception) || "-"
                    }}</span>
                  </div>
                </div>

                <v-divider class="my-2"></v-divider>
                <div class="mb-3">
                  <div
                    class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
                  >
                    Suelo
                  </div>
                  <div class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-weight</v-icon>
                    <span class="text-caption"
                      >{{ item.soilWeight || 0 }} g</span
                    >
                    <v-spacer></v-spacer>
                    <v-icon small class="mr-1">mdi-calendar</v-icon>
                    <span class="text-caption">{{
                      formatDate(item.soilReception) || "-"
                    }}</span>
                  </div>
                </div>

                <v-divider class="my-2"></v-divider>
                <div class="mb-3">
                  <div
                    class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
                  >
                    Dosis
                  </div>
                  <div class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-numeric</v-icon>
                    <span class="text-caption">{{
                      item.dosesQuantity || 0
                    }}</span>
                    <v-spacer></v-spacer>
                    <v-icon small class="mr-1">mdi-counter</v-icon>
                    <span class="text-caption">{{
                      item.doseSize
                        ? item.doseSize.map((d) => d.size).join(", ")
                        : "-"
                    }}</span>
                  </div>
                </div>

                <v-divider class="my-2"></v-divider>
                <div class="mb-3">
                  <div
                    class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
                  >
                    Cultivos
                  </div>
                  <div class="crop-chips">
                    <v-chip
                      v-for="(crop, index) in item.crops"
                      :key="index"
                      color="green lighten-2"
                      small
                      class="ma-1"
                    >
                      {{ truncate(getCrop(crop), 12) }}
                    </v-chip>
                    <span
                      v-if="!item.crops || item.crops.length === 0"
                      class="grey--text text-caption"
                      >-</span
                    >
                  </div>
                </div>

                <div class="mb-3">
                  <div
                    class="text-caption font-weight-bold grey--text text--darken-2 mb-1"
                  >
                    Marcadores
                  </div>
                  <div class="marker-chips">
                    <v-chip
                      v-for="(marker, index) in item.molecularMarkers"
                      :key="index"
                      color="deep-purple lighten-4"
                      small
                      class="ma-1"
                    >
                      {{
                        marker.markerType == "MO"
                          ? truncate(marker.microorganism_meta.name, 12)
                          : truncate(marker.soilFunction.es, 12)
                      }}
                    </v-chip>
                    <span
                      v-if="
                        !item.molecularMarkers ||
                        item.molecularMarkers.length === 0
                      "
                      class="grey--text text-caption"
                      >-</span
                    >
                  </div>
                </div>

                <v-divider class="my-2"></v-divider>
                <div class="d-flex justify-space-between">
                  <v-btn
                    small
                    color="primary"
                    @click="editItem(item)"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-pencil</v-icon>
                    Editar
                  </v-btn>

                  <v-menu offset-y left>
                    <template v-slot:activator="{ on }">
                      <v-btn small icon class="action-btn" v-on="on">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item
                        @click="results(item)"
                        :disabled="item.status == 'NEW'"
                      >
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="green">mdi-chart-bar</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Resultados</v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        @click="calibration(item)"
                        :disabled="item.status == 'NEW'"
                      >
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="orange">mdi-wrench</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Calibración</v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        @click="handleGeneratePackage(item)"
                        :disabled="
                          !!item.packageReference &&
                          item.packageReference.length > 0
                        "
                      >
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="primary">mdi-package</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Generar paquete</v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        @click="markAsSent(item)"
                        :disabled="!!item.sentNotification"
                      >
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="teal">mdi-send-check</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title
                          >Marcar como enviado</v-list-item-title
                        >
                      </v-list-item>

                      <v-divider class="my-1"></v-divider>

                      <v-list-item
                        @click="deleteItem(item)"
                        :disabled="item.status == 'FINISHED'"
                      >
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="red">mdi-delete</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Eliminar</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-alert v-else type="info" outlined class="mt-4">
            No se encontraron bioensayos
          </v-alert>
        </div>

        <v-pagination
          v-model="page"
          :length="numPages"
          :total-visible="Math.min(7, numPages)"
          color="primary"
          class="mt-4"
        ></v-pagination>
      </v-card-text>
    </v-card>

    <update-bioassays-modal
      v-model="showUpdateBioassaysModal"
      v-if="showUpdateBioassaysModal"
      @update="fetchData"
      @finish="handleFinishSettings"
    />
    <bioassays-results-modal
      v-model="showBioassaysResultsModal"
      @update="fetchData"
    />
    <ProductModal
      v-model="showProductModal"
      v-if="showProductModal"
      @close="handleProductClose"
    />
    <DeleteBioassayModal
      v-model="showDeleteBioassayModal"
      v-if="showDeleteBioassayModal"
      :title="`¿Estás seguro que desea eliminar el bioensayo '${bioassaySelected.serial}'?`"
      @confirmation="handleDeleteBioassay"
    />
    <CalibrationResultModal
      v-model="showCalibrationResultModal"
      v-if="showCalibrationResultModal"
      @update="fetchData"
    />
    <new-package-by-bioassays-modal
      v-model="showNewPackageByBioassaysModal"
      v-if="showNewPackageByBioassaysModal"
      @update_table="handleUpdateTable"
    />
    <SentPackageModal
      v-model="showSentPackageModal"
      v-if="showSentPackageModal"
      :title="`¿Estás seguro que desea marcar como enviado el paquete '${bioassaySelected.packageReference[0].name}'?`"
      @confirmation="handleSentPackage"
    />
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import BioassaysResultsModal from "../components/bioassays/modals/BioassaysResultsModal.vue";
import UpdateBioassaysModal from "../components/bioassays/modals/UpdateBioassaysModal.vue";
import ProductModal from "../components/products/modals/ProductModal.vue";
import DeleteBioassayModal from "../components/utils/GenericConfirmationModal.vue";
import CalibrationResultModal from "../components/bioassays/modals/CalibrationResultModal.vue";
import NewPackageByBioassaysModal from "../components/cmda_admin/modals/NewPackageByBioassaysModal.vue";
import SentPackageModal from "../components/utils/GenericConfirmationModal.vue";

export default {
  components: {
    UpdateBioassaysModal,
    BioassaysResultsModal,
    ProductModal,
    DeleteBioassayModal,
    CalibrationResultModal,
    NewPackageByBioassaysModal,
    SentPackageModal,
  },
  data() {
    return {
      search: "",
      enhancedHeaders: [
        {
          text: "ID",
          value: "serial",
          width: "100px",
          sortable: true,
        },
        {
          text: "Productos",
          value: "productInfo",
          width: "200px",
        },
        {
          text: "Suelo",
          value: "soilInfo",
          width: "120px",
        },
        {
          text: "Dosis",
          value: "doseInfo",
          width: "120px",
        },
        {
          text: "Réplicas",
          value: "replicatesPerDose",
          width: "90px",
          align: "center",
        },
        {
          text: "Cultivos / Marcadores",
          value: "biologicalInfo",
          width: "200px",
        },
        {
          text: "Inicio",
          value: "bioassayStartDate",
          width: "100px",
        },
        {
          text: "Estado",
          value: "status",
          width: "100px",
          align: "center",
        },
        {
          text: "",
          value: "actions",
          width: "50px",
          align: "center",
          sortable: false,
        },
      ],
      bioassays: [],
      fetchingData: false,
      per_page: 10,
      page: 1,
      numPages: 1,
      filter: {},
      // modals
      showUpdateBioassaysModal: false,
      showBioassaysResultsModal: false,
      showProductModal: false,
      showDeleteBioassayModal: false,
      showCalibrationResultModal: false,
      showNewPackageByBioassaysModal: false,
      showSentPackageModal: false,
    };
  },
  computed: {
    ...mapState("bioassays", ["bioassaySelected"]),
    tableHeight() {
      return this.$vuetify.breakpoint.mobile ? "auto" : "calc(100vh - 300px)";
    },
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    ...mapActions("bioassays", [
      "getBioassaysFiltered",
      "deleteBioassay",
      "setupPackageSend",
    ]),
    ...mapMutations("bioassays", ["SET_PRODUCT_ID", "SET_BIOASSAY"]),
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),

    truncate(value, length) {
      if (!value) return "";
      if (value.length <= length) return value;
      return value.substring(0, length) + "...";
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    editItem(item) {
      this.SET_BIOASSAY(item);
      this.showUpdateBioassaysModal = true;
    },

    results(item) {
      this.SET_BIOASSAY(item);
      this.showBioassaysResultsModal = true;
    },

    calibration(item) {
      this.SET_BIOASSAY(item);
      this.showCalibrationResultModal = true;
    },

    deleteItem(item) {
      this.SET_BIOASSAY(item);
      this.showDeleteBioassayModal = true;
    },

    markAsSent(item) {
      this.SET_BIOASSAY(item);
      this.showSentPackageModal = true;
    },
    async handleSentPackage() {
      try {
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        let reponse = await this.setupPackageSend({ params, body: {} });
        if (
          reponse.notificationCode &&
          (reponse.notificationCode.toString() == "202" ||
            reponse.notificationCode.toString() == "200")
        ) {
          this.SET_ALERT_TYPE({
            visible: true,
            type: "success",
            text: `Envio notificado exitosamente`,
            timeout: 5000,
          });
          await this.fetchData();
        } else {
          this.SET_ALERT_TYPE({
            visible: true,
            type: "warning",
            text: `Error al notificar envio a CMDA`,
            timeout: 7000,
          });
        }
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },
    getStatusColor(status) {
      const colorMap = {
        NEW: "grey",
        SETUP: "orange",
        UPLOADING_RESULTS: "blue",
        REVIEW: "primary",
        FINISHED: "green",
      };
      return colorMap[status] || "grey";
    },

    getStatus(status) {
      const statusMap = {
        NEW: "Nuevo",
        SETUP: "Configurado",
        UPLOADING_RESULTS: "Subiendo resultados",
        REVIEW: "Resultados PCR finalizado",
        FINISHED: "Finalizado",
      };
      return statusMap[status] || "Desconocido";
    },

    getStatusShort(status) {
      const shortMap = {
        NEW: "Nuevo",
        SETUP: "Conf.",
        UPLOADING_RESULTS: "Subiendo",
        REVIEW: "Revisión",
        FINISHED: "Finalizado",
      };
      return shortMap[status] || "?";
    },

    async fetchData() {
      try {
        this.fetchingData = true;
        let payload = {
          page: this.page,
          filter: this.filter,
          search: this.search,
          limit: this.per_page,
          population: "products",
        };
        let response = await this.getBioassaysFiltered(payload);

        this.numPages = response.bioassays.pages;
        this.bioassays = response.bioassays.items;
      } catch (error) {
        console.error(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.fetchingData = false;
      }
    },

    getCrop(value) {
      const cropMap = {
        olive: "Aceituna",
        chard: "Acelga",
        achiote: "Achiote",
        agave: "Agave",
        avocado: "Aguacate",
        garlic: "Ajo",
        sesame: "Ajonjolí",
        basil: "Albahaca",
        artichoke: "Alcachofa",
        alcatraz: "Alcatraz",
        shrunkenAlfalfa: "Alfalfa achicalada",
        greenAlfalfa: "Alfalfa verde",
        cotton: "Algodón",
        almond: "Almendro",
        birdseed: "Alpiste",
        amaranth: "Amaranto",
        anise: "Anís",
        celery: "Apio",
        blueberry: "Arándano",
        myrtle: "Arrayán",
        rice: "Arroz",
        greenForageOats: "Avena forrajera en verde",
        oatGrain: "Avena grano",
        bamboo: "Bambú",
        eggplant: "Berenjena",
        beetroot: "Betabel",
        broccoli: "Brocoli",
        peanut: "Cacahuate",
        cocoa: "Cacao",
        cherryCoffee: "Café cereza",
        pumpkin: "Calabaza",
        sweetPotato: "Camote",
        sugarCane: "Caña de azúcar",
        canola: "Canola",
        carambolo: "Carambolo",
        safflower: "Cártamo",
        barley: "Cebada",
        greenForageBarley: "Cebada forrajera en verde",
        onion: "Cebolla",
        chives: "Cebollín",
        ryeGrain: "Centeno grano",
        cherry: "Cereza",
        chabacano: "Chabacano",
        pea: "Chícharo",
        squash: "Chayote",
        chia: "Chía",
        chilacayote: "Chilacayote",
        chillyPepper: "Chile / Pimiento",
        custardApple: "Chirimoya",
        coriander: "Cilantro",
        plum: "Ciruela",
        coconutFruit: "Coco fruta",
        cabbage: "Col (repollo)",
        brusselsSprouts: "Col de Bruselas",
        cauliflower: "Coliflor",
        cumin: "Comino",
        chrysanthemum: "Crisantemo (planta)",
        peach: "Durazno",
        date: "Dátil",
        greenBean: "Ejote",
        cornCob: "Elote",
        dill: "Eneldo",
        epazote: "Epazote",
        asparagus: "Espárrago",
        spicesAndMedicinals: "Especias y medicinales",
        spinach: "Espinaca",
        raspberry: "Frambuesa",
        strawberry: "Fresa",
        bean: "Frijol",
        chickpea: "Garbanzo",
        sunflower: "Girasol",
        pomegranate: "Granada",
        soursop: "Guanábana",
        guava: "Guayaba",
        henequen: "Henequén",
        peppermint: "Hierbabuena",
        aromaticHerbs: "Hierbas aromáticas",
        fig: "Higo",
        jamaica: "Jamaica",
        ginger: "Jenjibre",
        jicama: "Jícama",
        lettuce: "Lechuga",
        lentil: "Lenteja",
        lime: "Lima",
        lemon: "Limón",
        linseed: "Linaza",
        litchi: "Litchi",
        corn: "Maíz",
        macadamia: "Macadamia",
        maguey: "Maguey",
        mamey: "Mamey",
        tangerine: "Mandarina",
        mango: "Mango",
        mangosteen: "Mangostán",
        chamomile: "Manzanilla",
        appleTree: "Manzano",
        passionFruit: "Maracuyá",
        daisyFlower: "Margarita",
        marjoram: "Mejorana",
        melon: "Melón",
        quince: "Membrillo",
        mint: "Menta",
        moringa: "Moringa",
        turnip: "Nabo",
        nanche: "Nanche",
        napa: "Napa",
        orange: "Naranja",
        medlar: "Níspero",
        nochebuena: "Nochebuena (planta)",
        walnut: "Nogal",
        noni: "Noni",
        nopal: "Nopal",
        oregano: "Orégano",
        ornamental: "Ornamentales",
        orchid: "Orquídea (planta)",
        oilPalm: "Palma africana o de aceite",
        potato: "Papa",
        papaya: "Papaya",
        grasslands: "Pastizales",
        matGrass: "Pasto tapete (m2)",
        pasturesAndMeadows: "Pastos y praderas",
        cucumber: "Pepino",
        pearTree: "Peral",
        parsley: "Perejil",
        peron: "Perón",
        persimony: "Persimonio",
        pepper: "Pimienta",
        pineapple: "Piña",
        pinion: "Piñón",
        pistache: "Pistache",
        pitaya: "Pitaya",
        banana: "Plátano",
        pore: "Poro",
        radish: "Rábano",
        rambutan: "Rambután",
        romerito: "Romerito",
        rosemary: "Romero",
        aloe: "Sábila",
        watermelon: "Sandía",
        sorghum: "Sorgo",
        soy: "Soya",
        stevia: "Stevia",
        tobacco: "Tabaco",
        tamarind: "Tamarindo",
        tejocote: "Tejocote",
        tomato: "Tomate Rojo (jitomate)",
        greenTomato: "Tomate Verde",
        thyme: "Tomillo",
        grapefruit: "Toronja (pomelo)",
        clover: "Trébol",
        wheat: "Trigo",
        dutchTulip: "Tulipán holandés",
        tuna: "Tuna",
        grape: "Uva",
        vanilla: "Vainilla",
        purslane: "Verdolaga",
        vine: "Vid",
        cassavaFood: "Yuca alimenticia",
        carrot: "Zanahoria",
        sapodilla: "Zapote",
        blackberry: "Zarzamora",
        zempoalxochitl: "Zempoalxochitl",
        fallow: "Barbecho",
        other: "Otro",
      };
      return cropMap[value] || "Sin cultivo";
    },

    handleProductSelected(productId) {
      this.SET_PRODUCT_ID(productId);
      this.showProductModal = true;
    },

    handleProductClose() {
      this.showProductModal = false;
      this.SET_PRODUCT_ID(null);
    },

    async handleFinishSettings() {
      this.showUpdateBioassaysModal = false;
      this.SET_BIOASSAY(null);
      await this.fetchData();
    },

    async handleDeleteBioassay() {
      try {
        let payload = {
          bioassayId: this.bioassaySelected._id,
        };
        await this.deleteBioassay(payload);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Eliminación exitosa`,
          timeout: 5000,
        });
        await this.fetchData();
      } catch (error) {
        console.error(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },

    handleGeneratePackage(item) {
      this.SET_BIOASSAY(item);
      this.showNewPackageByBioassaysModal = true;
    },
    async handleUpdateTable() {
      await this.fetchData();
      this.showNewPackageByBioassaysModal = false;
    },
  },
  watch: {
    async search() {
      this.page = 1;
      this.numPages = 1;
      await this.fetchData();
    },
    page() {
      this.fetchData();
    },
  },
};
</script>

<style scoped>
.bioassay-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.bioassay-table >>> thead tr th {
  background-color: #f5f7fa;
  font-weight: 600;
  font-size: 0.75rem;
  color: #374151 !important;
  position: sticky;
  top: 0;
  z-index: 2;
  height: 48px;
}

.bioassay-table >>> tbody tr {
  transition: background-color 0.2s;
}

.bioassay-table >>> tbody tr:hover {
  background-color: #f8fafc !important;
}

.bioassay-table >>> td {
  font-size: 0.75rem;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 8px 12px !important;
  vertical-align: top;
}

.product-chips,
.marker-chips,
.crop-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.v-chip {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: transform 0.2s;
}

.v-chip:hover {
  transform: translateY(-2px);
}

.status-chip {
  min-width: 70px;
  justify-content: center;
  font-weight: 500;
}

.status-chip-mobile {
  min-width: 60px;
  justify-content: center;
  font-weight: 500;
}

.action-btn {
  margin: 0 2px;
}

.text-no-wrap {
  white-space: nowrap;
}

.search-field {
  max-width: 300px;
}

/* Estilos para mobile */
.bioassay-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px !important;
  overflow: hidden;
}

.bioassay-card >>> .v-expansion-panel-header {
  min-height: 56px;
  padding: 0 16px;
}

.bioassay-card >>> .v-expansion-panel-content__wrap {
  padding: 0 16px 16px;
}

/* Efectos hover para mobile */
@media (hover: hover) {
  .bioassay-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
    transition: all 0.2s ease;
  }
}

/* Media queries para responsive */
@media (max-width: 1264px) {
  .bioassay-table >>> thead tr th,
  .bioassay-table >>> td {
    padding: 6px 8px !important;
  }

  .enhancedHeaders {
    font-size: 0.7rem;
  }
}

@media (max-width: 960px) {
  .search-field {
    max-width: 100%;
  }

  .v-card-title {
    padding: 16px;
  }
}

@media (max-width: 600px) {
  .bioassay-card >>> .v-expansion-panel-header {
    padding: 0 12px;
  }

  .bioassay-card >>> .v-expansion-panel-content__wrap {
    padding: 0 12px 12px;
  }

  .v-chip {
    font-size: 0.65rem;
    height: 24px;
  }
}
</style>
